/**
 * Assemble a move-out bundle, and open one that already exists.
 *
 * Assembly runs in an edge function because it reads two private buckets with
 * the service role and merges PDFs with pdf-lib. The browser only asks for it
 * and reports the outcome.
 *
 * A blocked bundle (409) is a normal outcome, not an error to bury: it means a
 * source document is missing, and the admin needs the name of it plus the way
 * to fix it. So the mutation resolves with `blocked` rather than throwing.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { supabaseAnonKey, supabaseUrl } from "@/lib/supabase";
import { BUNDLE_BUCKET } from "@/lib/depositSettlement";
import { depositSettlementKey } from "./useDepositSettlement";

export interface AssembleBundleResult {
  ok: boolean;
  blocked?: boolean;
  /** Present when blocked — the actionable reason, already in plain words. */
  error?: string;
  missing?: string[];
  /** The bundle already went out; replacing it needs a deliberate resend. */
  already_sent?: boolean;
  /** The deposit is decided; the bundle it was decided on is now immutable. */
  already_released?: boolean;
  settlement_id?: string;
  bundle_pdf_path?: string;
  page_count?: number;
  size_bytes?: number;
  recipient_email?: string | null;
  /** No email on the lessor record — the send step will need one entered. */
  recipient_missing?: boolean;
}

/**
 * Invoked with a raw fetch rather than functions.invoke.
 *
 * invoke() collapses a non-2xx into a generic FunctionsHttpError and discards
 * the response body, which is exactly where the blocked reason lives — the same
 * reason adminCreateContract calls the endpoint directly.
 */
async function invokeAssemble(
  bookingId: number
): Promise<AssembleBundleResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("Your session has expired. Sign in again.");

  const res = await fetch(
    `${supabaseUrl}/functions/v1/assemble-moveout-bundle`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
      },
      body: JSON.stringify({ booking_id: bookingId }),
    }
  );

  let payload: AssembleBundleResult | null = null;
  try {
    payload = (await res.json()) as AssembleBundleResult;
  } catch {
    payload = null;
  }

  // 409 covers three answers, not failures: a document is missing, the bundle
  // already went out, or the deposit is already decided. Only the first is
  // "blocked" — the other two are refusals to replace a document someone is
  // relying on, and the card must not offer a retry for them.
  if (res.status === 409 && payload) {
    return {
      ...payload,
      ok: false,
      blocked: !payload.already_sent && !payload.already_released,
    };
  }
  if (!res.ok) {
    throw new Error(payload?.error || `Bundle assembly failed (${res.status})`);
  }
  return payload ?? { ok: true };
}

export interface BundlePreview {
  to: string[];
  subject: string;
  html: string;
  delivery: "attach" | "link";
  attachment_filenames: string[];
  admin_copy_to: string[];
  admin_copy_used_fallback: boolean;
}

export interface SendBundleResult {
  ok: boolean;
  dry_run: boolean;
  preview?: BundlePreview;
  sent_at?: string;
  /** The mail is fine, but its attachment is not there — see `mode: "owner"`. */
  warning?: string;
  error?: string;
}

/**
 * Which of the two move-out mails to preview or send.
 *
 * "owner" is the completion notice sent after the refund is paid, with the
 * bundle attached. "representative" is the pre-refund bundle carrying the
 * release link, which the day-after-move-out tick sends on its own — it is kept
 * reachable here because the endpoint still serves it, not because the Move-out
 * tab offers it.
 */
export type MoveoutMailMode = "representative" | "owner";

/**
 * Preview or send one of the move-out emails.
 *
 * Both go through the same endpoint; `dry_run` is the only difference, so the
 * preview cannot drift from what is actually sent.
 */
async function invokeSend(input: {
  settlementId: string;
  dryRun: boolean;
  recipientEmail?: string | null;
  mode?: MoveoutMailMode;
}): Promise<SendBundleResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("Your session has expired. Sign in again.");

  const res = await fetch(`${supabaseUrl}/functions/v1/send-moveout-bundle`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      apikey: supabaseAnonKey,
    },
    body: JSON.stringify({
      settlement_id: input.settlementId,
      dry_run: input.dryRun,
      ...(input.mode ? { mode: input.mode } : {}),
      ...(input.recipientEmail
        ? { recipient_email: input.recipientEmail }
        : {}),
    }),
  });

  let payload: SendBundleResult | null = null;
  try {
    payload = (await res.json()) as SendBundleResult;
  } catch {
    payload = null;
  }
  if (!res.ok) {
    throw new Error(
      payload?.error ||
        (input.dryRun
          ? `Could not build the preview (${res.status})`
          : `Send failed (${res.status})`)
    );
  }
  return payload ?? { ok: true, dry_run: input.dryRun };
}

export function usePreviewMoveoutBundleEmail() {
  return useMutation({
    mutationFn: (input: {
      settlementId: string;
      recipientEmail?: string | null;
      mode?: MoveoutMailMode;
    }) => invokeSend({ ...input, dryRun: true }),
  });
}

export function useSendMoveoutBundle(bookingId: number | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      settlementId: string;
      recipientEmail?: string | null;
      mode?: MoveoutMailMode;
    }) => invokeSend({ ...input, dryRun: false }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: depositSettlementKey(bookingId),
      });
    },
  });
}

export function useAssembleMoveoutBundle(bookingId: number | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AssembleBundleResult> => {
      if (!bookingId) throw new Error("No booking selected");
      return invokeAssemble(bookingId);
    },
    onSettled: () => {
      // Refetch either way: a blocked attempt writes bundle_blocked_reason, so
      // the card has something new to show even when nothing was produced.
      void queryClient.invalidateQueries({
        queryKey: depositSettlementKey(bookingId),
      });
    },
  });
}

/**
 * A time-limited URL for a stored bundle.
 *
 * The bucket is private, so getPublicUrl would hand back a URL that 400s. Ten
 * minutes is enough to open it in a tab and matches the other signed-URL
 * surfaces in the admin UI.
 */
export async function getBundleSignedUrl(
  path: string,
  expiresInSeconds = 600
): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from(BUNDLE_BUCKET)
    .createSignedUrl(path, expiresInSeconds);
  if (error) return null;
  return data?.signedUrl ?? null;
}
